// This file is the main entry point into Vue.
// We're loading a bunch of different Vue components, some external, some internal.
// Internal ones (i.e. created by us) are configured at the bottom of this file.
// We're telling Vue that when it sees a certain HTML tag (e.g. `<multiple-choice>`)
// that it should instead use our component.

import Vue from 'vue';
import axios from 'axios';
import Toasted from 'vue-toasted';
import VModal from 'vue-js-modal';
import VTooltip from 'v-tooltip';

import Application from './Application.vue';
import store from './store';
import router from './router';

import Pretty from './filters/pretty';
import Highlight from './filters/highlight';

import Checkbox from './components/Checkbox.vue';
import Datetime from './components/Datetime';
import Spinner from './components/Spinner.vue';
import Upload from './components/Upload.vue';
import Account from './components/Account.vue';
import Header from './components/Header.vue';
import Configuration from './components/Configuration.vue';
import Profile from './components/Profile.vue';
import TOC from './components/TOC.vue';
import Simulation from './components/Simulation.vue';
import Components from './components/Components.vue';
import ProductMetadata from './components/ProductMetadata.vue';

import Media from './components/chapter/Media.vue';
import MultipleChoice from './components/chapter/MultipleChoice.vue';
import MultipleChoiceOption from './components/chapter/MultipleChoiceOption.vue';
import SimulationInteraction from './components/chapter/SimulationInteraction.vue';
import ZygoteInteraction from './components/chapter/ZygoteInteraction.vue';
import ZygoteMultipleChoice from './components/chapter/ZygoteMultipleChoice.vue';
import ZygoteSelectMultiple from './components/chapter/ZygoteSelectMultiple.vue';
import ZygoteOption from './components/chapter/ZygoteOption.vue';
import ZygoteRationale from './components/chapter/ZygoteRationale.vue';
import List from './components/chapter/List.vue';
import Footnote from './components/chapter/Footnote.vue';
import Example from './components/chapter/Example.vue';
import Problem from './components/chapter/Problem.vue';
import Exercise from './components/chapter/Exercise.vue';
import Formula from './components/chapter/Formula.vue';
import Note from './components/chapter/Note.vue';
import Anchor from './components/chapter/Anchor.vue';

import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';

Vue.config.productionTip = false;

Vue.http = Vue.prototype.$http = axios;
Vue.bus = Vue.prototype.$bus = new Vue();

Vue.http.defaults.baseURL = '/api';

Vue.http.interceptors.response.use(response => {
    if(!response.data.$data){
        console.warn(`Call to ${response.config.url} did not include $data.`);
    }

    return response.data;
}, error => {
    if(!error.response.data.$error){
        console.warn(`Error from ${error.config.url} did not include $error.`);
    }

    return Promise.reject(error.response.data);
});

Vue.use(VModal, {dynamic: true, injectModalsContainer: true, dynamicDefaults: {height: 'auto'}});

Vue.use(Toasted, {
    position: 'top-center',
    duration: 5000,
    containerClass: 'toast-container',
    className: 'toast',
    theme: 'bubble'
});

Vue.use(VTooltip);

require('./assets/less/global.less');

Vue.component('checkbox', Checkbox);
Vue.component('datetime', Datetime);
Vue.component('datetime-picker', VueCtkDateTimePicker);
Vue.component('spinner', Spinner);
Vue.component('primary-navigation', Header);
Vue.component('upload', Upload);
Vue.component('configuration', Configuration);
Vue.component('account', Account);
Vue.component('profile', Profile);
Vue.component('toc', TOC);
Vue.component('simulation', Simulation);
Vue.component('components', Components);
Vue.component('product-metadata', ProductMetadata);

Vue.component('media', Media);
Vue.component('list', List);
Vue.component('footnote', Footnote);
Vue.component('multiple-choice', MultipleChoice);
Vue.component('multiple-choice-option', MultipleChoiceOption);
Vue.component('simulation-interaction', SimulationInteraction);
Vue.component('zygote-interaction', ZygoteInteraction);
Vue.component('zygote-multiple-choice', ZygoteMultipleChoice);
Vue.component('zygote-select-multiple', ZygoteSelectMultiple);
Vue.component('zygote-option', ZygoteOption);
Vue.component('zygote-rationale', ZygoteRationale);
Vue.component('example', Example);
Vue.component('problem', Problem);
Vue.component('exercise', Exercise);
Vue.component('formula', Formula);
Vue.component('note', Note);
Vue.component('anchor', Anchor);

Vue.filter('pretty', Pretty);
Vue.filter('highlight', Highlight);

// We want to wait for VueX to initialize the user object as so many things depend on it that it would be pretty pointless
// to render the page before it was loaded. Basically, `user/load` checks to see if there is a token in localStorage and
// then makes a request to the API to load information about the user.
store.dispatch('user/load').then(() => new Vue({router, store, render: h => h(Application)}).$mount('#application'));
